/**
 * Los planes tal y como se anuncian en la web pública.
 *
 * Viven aquí, y no dentro de una página, porque los enseña tanto la portada como la pantalla de
 * `/planes` a la que apunta el código QR de los volantes. Con la lista duplicada bastaba con
 * cambiar un precio en un sitio y olvidarlo en el otro para que el volante impreso y la web
 * dijeran cosas distintas.
 *
 * Ojo: el precio va escrito ya formateado (`RD$ 1,149.99`) porque estas tarjetas solo lo muestran.
 * Los importes con los que se cobra están en `pages/plans/page.tsx`, que es la pantalla de
 * suscripción y necesita números para pasárselos a Stripe.
 */

export interface PlanPublico {
  id: string;
  category: 'contabilidad' | 'facturacion';
  name: string;
  /** Ya formateado para mostrar, no para calcular. */
  price: string;
  /** «/mes», o vacío en los prepago, que no se renuevan. */
  period: string;
  description?: string;
  features: string[];
  popular: boolean;
  /** Parada del degradado de Tailwind para la cabecera de la tarjeta. */
  color?: string;
  icon?: string;
  billingType?: 'subscription' | 'prepaid';
  /** Anunciado pero todavía no contratable: la tarjeta se ve apagada y sin enlace. */
  disabled?: boolean;
}

export const PLANES_PUBLICOS: PlanPublico[] = [
  {
    id: 'pyme',
    category: 'contabilidad',
    name: 'PYME',
    price: 'RD$ 1,149.99',
    period: '/mes',
    description: 'Ideal para pequeñas empresas que inician',
    features: [
      'Una empresa',
      'Facturación básica',
      'Dashboard básico',
      'Punto de venta (POS)',
      'Inventario para 500 productos',
      'Soporte por email',
      'Prueba gratis 7 días'
    ],
    popular: false,
    color: 'from-sky-500 to-sky-600',
    icon: 'ri-building-line',
  },
  {
    id: 'pro',
    category: 'contabilidad',
    name: 'PRO',
    price: 'RD$ 2,549.99',
    period: '/mes',
    description: 'Para empresas en crecimiento',
    features: [
      'Una empresa',
      'Punto de venta (POS)',
      'Contabilidad completa',
      'Dashboard básico',
      'Inventario limitado (2,000)',
      'Gestión bancaria básica',
      'Nómina básica (10 empleados)',
      'Soporte prioritario',
      'Prueba gratis 7 días'
    ],
    popular: true,
    color: 'from-blue-500 to-blue-600',
    icon: 'ri-rocket-line',
  },
  {
    id: 'plus',
    category: 'contabilidad',
    name: 'PLUS',
    price: 'RD$ 13,000.00',
    period: '/mes',
    description: 'Solución completa para empresas establecidas',
    features: [
      'Todas las funciones',
      'Módulo de Producción',
      'Dashboard KPI avanzado',
      'Inventario ilimitado',
      'Nómina completa',
      'Análisis financiero avanzado',
      'Reportes personalizados',
      'Soporte técnico especializado',
      'Soporte 24/7',
      'Prueba gratis 7 días'
    ],
    popular: false,
    color: 'from-purple-500 to-purple-600',
    icon: 'ri-vip-diamond-line',
  },
  {
    id: 'facturacion-150',
    category: 'facturacion',
    name: 'Plan 150 Facturas',
    price: 'RD$ 849.99',
    period: '/mes',
    description: 'El primer paso para dejar la factura en papel',
    features: [
      '1 Usuario',
      '150 Facturas al mes',
      'Cotizaciones',
      '2 Plantillas de factura',
      'Notas de crédito/débito',
      'Reportes básicos',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-blue-500 to-blue-600',
    icon: 'ri-file-text-line',
  },
  {
    id: 'facturacion-200',
    category: 'facturacion',
    name: 'Plan 200 Facturas',
    price: 'RD$ 1,149.99',
    period: '/mes',
    description: 'Para emprendedores y micro negocios que inician',
    features: [
      '1 Usuario',
      '200 Facturas al mes',
      'Cotizaciones',
      '2 Plantillas de factura',
      'Notas de crédito/débito',
      'Reportes básicos',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-blue-500 to-blue-600',
    icon: 'ri-file-text-line',
  },
  {
    id: 'facturacion-300',
    category: 'facturacion',
    name: 'Plan 300 Facturas',
    price: 'RD$ 1,800.00',
    period: '/mes',
    description: 'Para emprendedores y micro negocios que inician',
    features: [
      '1 Usuario',
      '300 Facturas al mes',
      'Cotizaciones',
      '2 Plantillas de factura',
      'Notas de crédito/débito',
      'Reportes básicos',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-blue-500 to-blue-600',
    icon: 'ri-file-text-line',
  },
  {
    id: 'facturacion-prepago-75',
    category: 'facturacion',
    name: 'Plan 75 Facturas',
    price: 'RD$ 749.99',
    period: '',
    description: 'Prepago de facturas para uso por consumo',
    features: [
      'Prepago',
      '75 Facturas',
      'Vigencia de un año',
      'Permite recargar facturas',
      'Creación de clientes'
    ],
    popular: false,
    color: 'from-emerald-500 to-emerald-600',
    icon: 'ri-bill-line',
    billingType: 'prepaid',
  },
  {
    id: 'facturacion-prepago-150',
    category: 'facturacion',
    name: 'Plan 150 Facturas',
    price: 'RD$ 1,149.99',
    period: '',
    description: 'Prepago de facturas para uso por consumo',
    features: [
      'Prepago',
      '150 Facturas',
      'Vigencia de un año',
      'Permite recargar facturas',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-emerald-500 to-emerald-600',
    icon: 'ri-bill-line',
    billingType: 'prepaid',
  },
  {
    id: 'facturacion-prepago-350',
    category: 'facturacion',
    name: 'Plan 350 Facturas',
    price: 'RD$ 1,849.99',
    period: '',
    description: 'Prepago de facturas para uso por consumo',
    features: [
      'Prepago',
      '350 Facturas',
      'Vigencia de un año',
      'Permite recargar facturas',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-emerald-500 to-emerald-600',
    icon: 'ri-bill-line',
    billingType: 'prepaid',
  },
  {
    id: 'facturacion-prepago-750',
    category: 'facturacion',
    name: 'Plan 750 Facturas',
    price: 'RD$ 3,500.00',
    period: '',
    description: 'Prepago de facturas para uso por consumo',
    features: [
      'Prepago',
      '750 Facturas',
      'Vigencia de un año',
      'Permite recargar facturas',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-emerald-500 to-emerald-600',
    icon: 'ri-bill-line',
    billingType: 'prepaid',
  },
  {
    id: 'facturacion-500',
    category: 'facturacion',
    name: 'Plan 500 Facturas',
    price: 'RD$ 2,149.99',
    period: '/mes',
    description: 'Para negocios en crecimiento con más volumen de facturación',
    features: [
      '1 Usuario',
      '500 Facturas al mes',
      'Cotizaciones',
      '2 Plantillas de factura',
      'Notas de crédito/débito',
      'Reportes básicos',
      'Creación de clientes',
      'Inventario básico'
    ],
    popular: false,
    color: 'from-blue-500 to-blue-600',
    icon: 'ri-file-text-line',
  },
  {
    id: 'facturacion-1000',
    category: 'facturacion',
    name: 'Plan 1000 Facturas',
    price: 'RD$ 4,500.00',
    period: '/mes',
    description: 'Para empresas con alta demanda de facturación mensual',
    features: [
      '1 Usuario',
      '1000 Facturas al mes',
      'Cotizaciones',
      '2 Plantillas de factura',
      'Notas de crédito/débito',
      'Reportes básicos',
      'Creación de clientes',
      'Inventario de productos',
      'Reportes de inventario'
    ],
    popular: false,
    color: 'from-blue-500 to-blue-600',
    icon: 'ri-file-text-line',
  },
  {
    id: 'facturacion-simple',
    category: 'facturacion',
    name: 'Plan 2500 Facturas',
    price: 'RD$ 5,800.00',
    period: '/mes',
    description: 'Para operaciones establecidas que requieren mayor capacidad',
    features: [
      '1 Usuario',
      '2500 Facturas al mes',
      'Cotizaciones',
      '2 Plantillas de factura',
      'Notas de crédito/débito',
      'Reportes básicos',
      'Creación de clientes',
      'Inventario de productos',
      'Reportes de inventario'
    ],
    popular: false,
    color: 'from-teal-500 to-teal-600',
    icon: 'ri-file-text-line',
  },
  {
    id: 'estudiantil',
    category: 'contabilidad',
    name: 'ESTUDIANTIL',
    price: 'RD$ 6,399.36',
    period: '/mes',
    features: [
      'Empresas ilimitadas',
      'Todas las funciones',
      'Dashboard KPI avanzado',
      'Inventario ilimitado',
      'Análisis financiero avanzado',
      'Descuento estudiantil',
      'Prueba gratis 7 días'
    ],
    popular: false
  }
];

/**
 * ESTUDIANTIL queda fuera de los tres grupos: se contrata por verificación académica
 * (`/plans/student-verify`), no eligiéndolo en una parrilla de precios.
 */
/**
 * PYME, PRO y PLUS (los planes con POS) están ocultos por ahora, tanto en la web pública como en
 * la pantalla de suscripción de dentro. No se borran porque hay cuentas que los tienen contratados
 * y sus permisos siguen dependiendo de ellos. Para volver a enseñarlos, ponerlo en `true`.
 */
export const MOSTRAR_PLANES_POS = false;

const visibles = PLANES_PUBLICOS.filter((p) => p.name !== 'ESTUDIANTIL');

export const PLANES_CONTABILIDAD = visibles.filter((p) => p.category === 'contabilidad');

const facturacion = visibles.filter((p) => p.category === 'facturacion');

/** Facturación por suscripción mensual. */
export const PLANES_FACTURACION = facturacion.filter((p) => p.billingType !== 'prepaid');

/** Paquetes de facturas que se compran una vez y valen un año. */
export const PLANES_PREPAGO = facturacion.filter((p) => p.billingType === 'prepaid');
